import { useCallback, useRef, useState } from "react";
import { toast } from "@volca/ui/lib/toast";
import { getPayrollRosterApiErrorMessage } from "@/payroll/roster/api-errors";
import {
  useDiscardPayrollRosterDraft,
  usePublishPayrollRoster,
  useSavePayrollRosterDraft,
  useSyncPayrollRosterTechnicians,
} from "@/payroll/roster/query";
import type {
  PayrollRosterDraftMember as DraftMember,
  PayrollRosterEditTarget as EditTarget,
  PayrollRosterField,
  PayrollRosterMember,
  PayrollRosterRowFocus,
} from "@/payroll/roster/types";
import {
  applyBulkImportToMembers,
  type BulkImportRow,
} from "@/payroll/roster/utils";

interface UseRosterEditorActionsOptions {
  businessId: string;
  fields: PayrollRosterField[];
  draftMembers: DraftMember[];
  suggestionDrafts: DraftMember[];
  dismissedExternalIds: string[];
  hasErrors: boolean;
  setRowFocus: (focus: PayrollRosterRowFocus) => void;
}

export const useRosterEditorActions = ({
  businessId,
  fields,
  draftMembers,
  suggestionDrafts,
  dismissedExternalIds,
  hasErrors,
  setRowFocus,
}: UseRosterEditorActionsOptions) => {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [dismissingSuggestionId, setDismissingSuggestionId] = useState<
    string | null
  >(null);
  const isDismissingSuggestion = dismissingSuggestionId != null;
  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const { mutateAsync: saveRosterDraft, isPending: isSavingDraft } =
    useSavePayrollRosterDraft(businessId);
  const publishRoster = usePublishPayrollRoster(businessId);
  const discardDraft = useDiscardPayrollRosterDraft(businessId);
  const syncTechnicians = useSyncPayrollRosterTechnicians(businessId);
  const mutatingRef = useRef(false);
  const rosterMutationPending =
    isDismissingSuggestion ||
    isSavingDraft ||
    publishRoster.isPending ||
    discardDraft.isPending ||
    syncTechnicians.isPending;

  const persistDraft = useCallback(
    async (nextDraftMembers: DraftMember[], nextDismissed?: string[]) => {
      if (mutatingRef.current) return false;

      mutatingRef.current = true;
      try {
        const memberExternalIds = new Set(
          nextDraftMembers.map(
            ({ member }) => member.service_titan_employee_id,
          ),
        );
        await saveRosterDraft({
          roster: {
            fields,
            members: nextDraftMembers.map(({ member }) => member),
            removed_member_external_ids: (
              nextDismissed ?? dismissedExternalIds
            ).filter((externalId) => !memberExternalIds.has(externalId)),
          },
        });
        return true;
      } catch (error) {
        toast.error("Changes could not be saved", {
          description: getPayrollRosterApiErrorMessage(error),
        });
        return false;
      } finally {
        mutatingRef.current = false;
      }
    },
    [dismissedExternalIds, fields, saveRosterDraft],
  );

  const openDraft = useCallback((draft: DraftMember) => {
    if (mutatingRef.current) return;
    setReviewOpen(false);
    setEditTarget(
      draft.suggestion
        ? { mode: "suggestion", userId: draft.suggestion.user_id }
        : { mode: "edit", draftId: draft.id },
    );
  }, []);

  const openAdd = () => {
    if (mutatingRef.current) return;
    setReviewOpen(false);
    setEditTarget({ mode: "new" });
  };

  const closeDrawer = () => setEditTarget(null);

  const saveEdit = async (member: PayrollRosterMember) => {
    if (!editTarget || mutatingRef.current) return;
    const adding =
      editTarget.mode === "new" || editTarget.mode === "suggestion";
    const next = adding
      ? [
          ...draftMembers,
          { id: `new-${crypto.randomUUID()}`, isNew: true, member },
        ]
      : draftMembers.map((draft) =>
          draft.id === editTarget.draftId ? { ...draft, member } : draft,
        );
    if (await persistDraft(next)) setEditTarget(null);
  };

  const removeMember = async () => {
    if (!editTarget || mutatingRef.current) return;
    if (editTarget.mode !== "edit") {
      setEditTarget(null);
      return;
    }
    const { draftId } = editTarget;
    const next = draftMembers.filter((draft) => draft.id !== draftId);
    if (await persistDraft(next)) setEditTarget(null);
  };

  const dismissSuggestion = useCallback(
    async (draft: DraftMember | undefined) => {
      if (mutatingRef.current) return;

      const suggestion = draft?.suggestion;
      if (!suggestion) return;

      setDismissingSuggestionId(suggestion.service_titan_employee_id);
      try {
        const next = [
          ...dismissedExternalIds,
          suggestion.service_titan_employee_id,
        ];
        if (await persistDraft(draftMembers, next)) {
          if (suggestionDrafts.length === 1) setRowFocus(null);
          setEditTarget(null);
        }
      } finally {
        setDismissingSuggestionId(null);
      }
    },
    [
      dismissedExternalIds,
      draftMembers,
      persistDraft,
      setRowFocus,
      suggestionDrafts.length,
    ],
  );

  const applyImport = async (
    field: PayrollRosterField,
    rows: BulkImportRow[],
  ) => {
    const { members, matched, unmatched, invalid } = applyBulkImportToMembers(
      draftMembers.map(({ member }) => member),
      field,
      rows,
    );
    if (matched.length === 0) return { matched, unmatched, invalid };

    const next = draftMembers.map((draft, index) => ({
      ...draft,
      member: members[index],
    }));
    const saved = await persistDraft(next);
    if (!saved) throw new Error("Roster import could not be saved");
    return { matched, unmatched, invalid };
  };

  const openReview = () => {
    if (mutatingRef.current) return;
    if (hasErrors) {
      toast.error("Complete the required roster fields before publishing.");
      return;
    }
    setEditTarget(null);
    setReviewOpen(true);
  };

  const publish = async () => {
    if (mutatingRef.current) return;
    mutatingRef.current = true;
    try {
      await publishRoster.mutateAsync();
      setEditTarget(null);
      setReviewOpen(false);
      toast.success("Payroll roster published");
    } catch (error) {
      toast.error("Payroll roster could not be published", {
        description: getPayrollRosterApiErrorMessage(error),
      });
    } finally {
      mutatingRef.current = false;
    }
  };

  const discard = () => {
    if (mutatingRef.current) return;
    mutatingRef.current = true;
    discardDraft.mutate(undefined, {
      onSuccess: () => {
        setEditTarget(null);
        setReviewOpen(false);
        toast.success("Draft changes discarded");
      },
      onError: (error) => {
        toast.error("Changes could not be discarded", {
          description: getPayrollRosterApiErrorMessage(error),
        });
      },
      onSettled: () => {
        mutatingRef.current = false;
      },
    });
  };

  const syncUsers = async () => {
    if (mutatingRef.current) return;
    mutatingRef.current = true;
    syncTechnicians.reset();
    try {
      await syncTechnicians.mutateAsync();
      toast.success("ServiceTitan technicians synced");
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      toast.error("ServiceTitan technicians could not be synced", {
        description: getPayrollRosterApiErrorMessage(error),
      });
    } finally {
      mutatingRef.current = false;
    }
  };

  return {
    editTarget,
    setEditTarget,
    reviewOpen,
    setReviewOpen,
    dismissingSuggestionId,
    isDismissingSuggestion,
    isSavingDraft,
    publishRoster,
    discardDraft,
    syncTechnicians,
    mutatingRef,
    rosterMutationPending,
    openDraft,
    openAdd,
    closeDrawer,
    saveEdit,
    removeMember,
    dismissSuggestion,
    applyImport,
    openReview,
    publish,
    discard,
    syncUsers,
  };
};
